import { z } from 'zod';
import { ClassIdSchema, PlayableClassIdSchema } from '../data/classes.js';
import { DEFAULT_LOOK, LookSchema } from '../data/look.js';
import { AppearanceSchema } from './appearance.js';
/**
 * Guest onboarding: a nickname is the whole sign-up (CADRAGE, "Immédiat").
 * The server mints an account, a character and a signed token; the client keeps
 * the token in localStorage and reconnects with it.
 */
export const NICKNAME_MIN = 2;
export const NICKNAME_MAX = 16;
/**
 * Letters, digits, one inner hyphen or underscore. Deliberately narrow: a
 * nickname is shown to other players, so lookalike and zero-width characters
 * are kept out rather than filtered later.
 */
const NICKNAME_PATTERN = /^[\p{L}\p{N}]+(?:[-_][\p{L}\p{N}]+)*$/u;
export const NicknameSchema = z
    .string()
    .trim()
    .min(NICKNAME_MIN, `Le pseudo fait au moins ${String(NICKNAME_MIN)} caractères`)
    .max(NICKNAME_MAX, `Le pseudo fait au plus ${String(NICKNAME_MAX)} caractères`)
    .regex(NICKNAME_PATTERN, 'Lettres et chiffres, avec - ou _ à l’intérieur');
export const GuestAuthRequestSchema = z.object({
    nickname: NicknameSchema,
    classId: PlayableClassIdSchema,
    /** Hair and eye colour; a client from before the choice existed gets the defaults. */
    look: LookSchema.default(DEFAULT_LOOK),
});
/** An instance of a zone (ADR 0068): drawn by the server, given to the characters it admits. */
export const InstanceIdSchema = z.string().regex(/^[a-z0-9-]{8,40}$/);
export const AuthenticatedCharacterSchema = z.object({
    id: z.uuid(),
    name: NicknameSchema,
    classId: ClassIdSchema,
    zoneId: z.string().min(1),
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    level: z.number().int().min(1),
    xp: z.number().int().min(0),
    /** Saved health; null means full (a fresh character, or one saved before health existed). */
    hp: z.number().int().min(0).nullable(),
    look: LookSchema.default(DEFAULT_LOOK),
    /**
     * The instance of `zoneId` the character was let into (ADR 0068), when that
     * zone is an instance: told by `zone_change`, kept by the client for a
     * reload. Never from the server's own records.
     */
    instanceId: InstanceIdSchema.optional(),
});
export const AuthResponseSchema = z.object({
    /** Signed session token; the client sends it back when joining a room. */
    token: z.string().min(1),
    expiresAt: z.number().int().positive(),
    character: AuthenticatedCharacterSchema,
});
/**
 * Account consolidation (CADRAGE: "comptes persistants"). A guest turns their
 * account into a real one with an email and a password; a real account logs
 * in, owns several characters and picks one per session.
 */
export const EmailSchema = z
    .email('Adresse e-mail invalide')
    .max(254)
    .transform((value) => value.trim().toLowerCase());
export const PASSWORD_MIN = 8;
export const PasswordSchema = z
    .string()
    .min(PASSWORD_MIN, `Le mot de passe fait au moins ${String(PASSWORD_MIN)} caractères`)
    .max(128, 'Le mot de passe fait au plus 128 caractères');
export const UpgradeAccountRequestSchema = z.object({
    email: EmailSchema,
    password: PasswordSchema,
});
export const LoginRequestSchema = z.object({ email: EmailSchema, password: z.string().min(1) });
/**
 * Password reset (ADR 0056): an address in, a mailed link out; the link's
 * token and a new password in, an account session out. The first answer
 * never says whether the address is known.
 */
export const ForgotPasswordRequestSchema = z.object({ email: EmailSchema });
export const ResetPasswordRequestSchema = z.object({
    token: z.string().min(1).max(1024),
    password: PasswordSchema,
});
export const MAX_CHARACTERS_PER_ACCOUNT = 5;
export const CreateCharacterRequestSchema = z.object({
    nickname: NicknameSchema,
    classId: PlayableClassIdSchema,
    look: LookSchema.default(DEFAULT_LOOK),
});
export const SelectCharacterRequestSchema = z.object({ characterId: z.uuid() });
/** Deleting a character: the player types its name again, so a slip of the mouse deletes nothing. */
export const DeleteCharacterRequestSchema = z.object({ name: z.string().min(1).max(64) });
/**
 * Deleting the whole account (ADR 0075, App Store rule 5.1.1(v)): the client
 * has the player type a word first; the server only wants this explicit yes.
 */
export const DeleteAccountRequestSchema = z.object({ confirm: z.literal(true) });
/** A character as listed on the selection screen: identity plus what it wears, for the preview. */
export const CharacterSummarySchema = AuthenticatedCharacterSchema.extend({
    appearance: AppearanceSchema,
});
/**
 * Sign-in through another service (ADR 0054). Google by OpenID Connect
 * (authorization code, exchanged by the server), Steam by OpenID 2.0. Both
 * leave the page and come back to the client's origin with a `state` the
 * server signed; the client hands the query string back to the server.
 */
export const SIGN_IN_PROVIDERS = ['google', 'steam'];
export const SignInProviderSchema = z.enum(SIGN_IN_PROVIDERS);
/** Which providers this server offers, read by the welcome screen. */
export const SignInProvidersResponseSchema = z.object({
    providers: z.array(SignInProviderSchema),
});
/** Leave for the provider: to sign in, or (with a session) to link it to this account. */
export const SignInStartRequestSchema = z.object({
    provider: SignInProviderSchema,
    link: z.boolean().default(false),
});
export const SignInStartResponseSchema = z.object({ url: z.url() });
/** Back from the provider: the page's query string, as is. The server reads only what it knows. */
export const SignInFinishRequestSchema = z.object({
    params: z.record(z.string().max(64), z.string().max(4096)),
});
export const AccountSessionSchema = z.object({
    /** Token naming the account, not a character: it lists and creates characters, it does not join zones. */
    accountToken: z.string().min(1),
    expiresAt: z.number().int().positive(),
    /** Null for an account known only by Steam, which gives no address. */
    email: EmailSchema.nullable(),
    /** The services this account signs in with. */
    providers: z.array(SignInProviderSchema).default([]),
    characters: z.array(CharacterSummarySchema),
});
/**
 * The roster as either session kind sees it. A guest has an account too, with
 * no email and no provider: the client offers to secure it exactly then.
 */
export const RosterSchema = z.object({
    email: EmailSchema.nullable(),
    providers: z.array(SignInProviderSchema).default([]),
    characters: z.array(CharacterSummarySchema),
});
/** What a finished sign-in answers: a session, or the provider now linked to the session's account. */
export const SignInFinishResponseSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('session'), session: AccountSessionSchema }),
    z.object({
        kind: z.literal('linked'),
        provider: SignInProviderSchema,
        email: EmailSchema.nullable(),
        providers: z.array(SignInProviderSchema),
    }),
]);
/** Whether an account is only a guest's: nothing but its token finds it again. */
export function isGuestAccount(account) {
    return account.email === null && account.providers.length === 0;
}
/** What a client sends to join a zone. Nothing but the token: no self-reported position. */
export const JoinZoneOptionsSchema = z.object({
    token: z.string().min(1),
    /**
     * Which zone room to be matched into (rooms are split by it). Only routing:
     * the room still checks the character is saved in its zone.
     */
    zoneId: z.string().min(1).max(32).optional(),
    /**
     * Which instance of an instanced zone (ADR 0068): rooms of such a zone are
     * split by it too. Only routing: the room checks the character was admitted.
     */
    instanceId: InstanceIdSchema.optional(),
});
//# sourceMappingURL=auth.js.map